import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ScheduleItem,
  type ScheduleSettings,
  type HolidayInfo,
  type ScheduleHealthSummary,
  formatDateFull,
  formatDateLabel,
  toISODate,
  getClientHealthLabel,
  getHealthSummaryColor,
} from '@/lib/scheduleUtils';
import {
  inferMilestones,
  getCurrentStage,
  getNextStage,
  getEstimatedCompletionDate,
  getBaselineCompletionDate,
  getProjectDelayExplanation,
  getClientScheduleStatus,
  getUpcomingScheduleItems,
  getCompletedScheduleItems,
  getScheduleProgress,
  getLinkedStageNames,
  filterItemsByRole,
} from '@/lib/scheduleDataUtils';
import ClientScheduleUpdates from './ClientScheduleUpdates';
import { CONSTRUCTION_SUB_STAGE_ORDER, CONSTRUCTION_SUB_STAGE_CONFIG, inferConstructionSubStage } from '@/hooks/useStageScheduleChecklist';

const STAGE_ORDER = ['Planning', 'Design', 'Consent', 'Construction', 'Handover'];
const STAGE_CONFIG: Record<string, { icon: string; accent: string; bg: string }> = {
  Planning:     { icon: 'ri-map-pin-line',      accent: '#D97706', bg: '#FFFBEB' },
  Design:       { icon: 'ri-pencil-ruler-line', accent: '#0D9488', bg: '#ECFDF5' },
  Consent:      { icon: 'ri-file-shield-line',  accent: '#E8621A', bg: '#FEF0E7' },
  Construction: { icon: 'ri-building-2-line',   accent: '#059669', bg: '#F0FDF4' },
  Handover:     { icon: 'ri-key-2-line',        accent: '#10B981', bg: '#ECFDF5' },
};

function inferStageFromTitle(title: string): string {
  const lower = title.toLowerCase();
  if (/\b(precon|contract|owner|site prep|service disconnect|h&s|health and safety|setup|kickoff|application|deposit)\b/.test(lower)) return 'Planning';
  if (/\b(design|engineering|geotech|drawings?|specification|architect|structural engineer)\b/.test(lower)) return 'Design';
  if (/\b(epa|council|cc inspection|eg inspection|approval|compliance|consent|building consent|resource consent)\b/.test(lower)) return 'Consent';
  if (/\b(demolition|excavation|foundation|slab|drain|plumbing|frame|truss|roof|cladding|gib|paint|tile|kitchen|flooring|electrical|interior|clean|wrap|window|brick|plaster|retaining wall|joist|steel)\b/.test(lower)) return 'Construction';
  if (/\b(final inspection|owner inspection|warranty|photo|ccc|handover|defect|maintenance period|practical completion|insurance change)\b/.test(lower)) return 'Handover';
  return 'Planning';
}

interface Props {
  items: ScheduleItem[];
  settings: ScheduleSettings;
  holidays: HolidayInfo[];
  healthSummary: ScheduleHealthSummary | null;
  criticalItemIds: Set<string>;
  projectEndDate: string | null;
  loading: boolean;
}

export default function ScheduleClientView({
  items,
  healthSummary,
  criticalItemIds,
  projectEndDate,
  loading,
}: Props) {
  const { t } = useTranslation();
  const [expandedSection, setExpandedSection] = useState<string | null>('milestones');
  const [selectedItem, setSelectedItem] = useState<ScheduleItem | null>(null);

  const today = toISODate(new Date());

  // Client-filtered items (hide internal small tasks)
  const visibleItems = useMemo(() => {
    return filterItemsByRole(items, 'client', criticalItemIds);
  }, [items, criticalItemIds]);

  const included = useMemo(() => items.filter((i) => i.is_included), [items]);

  // Build stage progress from items using main_stage
  const stageProgress = useMemo(() => {
    const map = new Map<string, { total: number; completed: number; status: string }>();
    for (const name of STAGE_ORDER) {
      map.set(name, { total: 0, completed: 0, status: 'Upcoming' });
    }
    for (const item of included) {
      const stageName = item.main_stage || (item.phase && STAGE_CONFIG[item.phase] ? item.phase : inferStageFromTitle(item.title));
      const s = map.get(stageName);
      if (!s) continue;
      s.total += 1;
      if (item.is_complete || item.progress >= 100) {
        s.completed += 1;
      }
    }
    for (const [name, data] of map) {
      if (data.total === 0) {
        data.status = 'Upcoming';
      } else if (data.completed === data.total) {
        data.status = 'Completed';
      } else if (data.completed > 0) {
        data.status = 'In Progress';
      } else {
        // Check if any earlier stage is not complete
        const idx = STAGE_ORDER.indexOf(name);
        let earlierIncomplete = false;
        for (let i = 0; i < idx; i++) {
          const earlier = map.get(STAGE_ORDER[i]);
          if (earlier && earlier.total > 0 && earlier.completed < earlier.total) {
            earlierIncomplete = true;
            break;
          }
        }
        data.status = earlierIncomplete ? 'Upcoming' : 'In Progress';
      }
    }
    return STAGE_ORDER.map((name) => ({ name, ...map.get(name)! }));
  }, [included]);

  // Construction sub-stage progress for client
  const constructionSubProgress = useMemo(() => {
    const subs = new Map<string, { total: number; completed: number }>();
    for (const sub of CONSTRUCTION_SUB_STAGE_ORDER) {
      subs.set(sub, { total: 0, completed: 0 });
    }
    for (const item of included) {
      const mainStage = item.main_stage || item.phase;
      if (mainStage !== 'Construction') continue;
      const sub = item.construction_sub_stage || inferConstructionSubStage(item.title);
      if (!sub || !subs.has(sub)) continue;
      const d = subs.get(sub)!;
      d.total += 1;
      if (item.is_complete || item.progress >= 100) d.completed += 1;
    }
    return CONSTRUCTION_SUB_STAGE_ORDER
      .filter((sub) => (subs.get(sub)?.total || 0) > 0)
      .map((sub) => ({ name: sub, ...subs.get(sub)! }));
  }, [included]);

  const { pct: progress, completed: completedCount, total: totalStages } = useMemo(
    () => getScheduleProgress(included),
    [included]
  );

  const currentStage = useMemo(() => getCurrentStage(included), [included]);
  const nextStage = useMemo(() => getNextStage(included, currentStage), [included, currentStage]);
  const estCompletion = useMemo(() => projectEndDate || getEstimatedCompletionDate(included), [projectEndDate, included]);
  const baselineEnd = useMemo(() => getBaselineCompletionDate(included), [included]);
  const delayExplanation = useMemo(() => getProjectDelayExplanation(included), [included]);
  const status = useMemo(() => getClientScheduleStatus(included), [included]);

  // Milestones inferred
  const milestones = useMemo(() => inferMilestones(included, criticalItemIds, 8), [included, criticalItemIds]);

  // Recently completed
  const recentlyCompleted = useMemo(() => getCompletedScheduleItems(included, 4), [included]);

  // Upcoming
  const upcomingItems = useMemo(() => getUpcomingScheduleItems(included, 8), [included]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-[#059669] text-3xl"></i>
          <p className="text-[#9CA3AF] text-sm mt-3">{t('clientView.loadingSchedule')}</p>
        </div>
      </div>
    );
  }

  if (included.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-2xl bg-[#F9FAFB]">
          <i className="ri-calendar-schedule-line text-[#D1D5DB] text-3xl"></i>
        </div>
        <h3 className="text-[#1A1A1A] font-bold text-base mb-2">{t('clientView.scheduleComingSoon')}</h3>
        <p className="text-[#9CA3AF] text-sm max-w-xs mx-auto">
          {t('clientView.scheduleComingSoonDesc')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Simplified Stage Timeline ── */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <h3 className="text-sm font-bold text-[#1A1A1A] mb-3">{t('clientView.stageProgress')}</h3>
        <div className="space-y-2">
          {stageProgress.map((stage) => {
            const cfg = STAGE_CONFIG[stage.name];
            const isCompleted = stage.status === 'Completed';
            const isInProgress = stage.status === 'In Progress';
            return (
              <div key={stage.name} className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: cfg.bg }}
                >
                  <i
                    className={`${isCompleted ? 'ri-check-line' : cfg.icon} text-sm`}
                    style={{ color: isCompleted ? '#059669' : cfg.accent }}
                  ></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-semibold ${isCompleted ? 'text-[#9CA3AF]' : isInProgress ? 'text-[#1A1A1A]' : 'text-[#9CA3AF]'}`}>
                      {stage.name}
                    </span>
                    <span
                      className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: isCompleted ? '#F0FDF4' : isInProgress ? '#FEF0E7' : '#F9FAFB',
                        color: isCompleted ? '#059669' : isInProgress ? '#E8621A' : '#9CA3AF',
                      }}
                    >
                      {stage.status}
                    </span>
                  </div>
                  {stage.total > 0 && (
                    <div className="mt-1">
                      <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${stage.total > 0 ? Math.round((stage.completed / stage.total) * 100) : 0}%`,
                            backgroundColor: isCompleted ? '#059669' : cfg.accent,
                          }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Construction Milestone Progress (client view) ── */}
      {constructionSubProgress.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-[#1A1A1A] mb-3">{t('clientView.constructionMilestones')}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {constructionSubProgress.map((sub) => {
              const cfg = CONSTRUCTION_SUB_STAGE_CONFIG[sub.name];
              const pct = sub.total > 0 ? Math.round((sub.completed / sub.total) * 100) : 0;
              const isComplete = pct === 100;
              return (
                <div key={sub.name} className="rounded-xl border border-gray-100 p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: cfg.bgColor }}>
                      <i className={`${cfg.icon} text-xs`} style={{ color: cfg.accentColor }}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-[#374151]">{sub.name}</p>
                      {cfg.paymentPercent > 0 && (
                        <p className="text-[9px] text-[#0891B2] font-medium">{t('clientView.paymentPercent', { n: cfg.paymentPercent })}</p>
                      )}
                    </div>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: isComplete ? '#059669' : cfg.accentColor }}></div>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[9px] text-[#9CA3AF]">{sub.completed}/{sub.total}</span>
                    <span className="text-[9px] font-bold" style={{ color: isComplete ? '#059669' : '#374151' }}>{pct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Project Timeline Summary ── */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[#9CA3AF] text-xs font-bold uppercase tracking-wider mb-1">{t('clientView.overallProgress')}</p>
            <h2 className="text-[#1A1A1A] text-3xl font-bold">{progress}%</h2>
          </div>
          <div
            className="px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap"
            style={{ backgroundColor: status.bg, color: status.color }}
          >
            {status.label}
          </div>
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden mb-3">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${progress}%`, backgroundColor: status.color }}
          ></div>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-[#9CA3AF]">
            {t('clientView.stagesCompleted', { completed: completedCount, total: totalStages })}
          </span>
          {estCompletion && (
            <span className="text-[#374151] font-semibold">
              {t('clientView.expectedCompletion')}: {formatDateLabel(estCompletion)}
            </span>
          )}
        </div>
      </div>

      {/* ── Key Dates Row ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white rounded-2xl border border-gray-100 p-4 text-center">
          <div className="text-[10px] text-[#9CA3AF] font-semibold uppercase tracking-wider mb-1">{t('clientView.currentStage')}</div>
          {currentStage ? (
            <div>
              <p className="text-sm font-bold text-[#E8621A]">{currentStage.title}</p>
              {currentStage.phase && <p className="text-[10px] text-[#9CA3AF] mt-0.5">{currentStage.phase}</p>}
            </div>
          ) : (
            <p className="text-sm text-[#9CA3AF]">{t('clientView.projectComplete')}</p>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4 text-center">
          <div className="text-[10px] text-[#9CA3AF] font-semibold uppercase tracking-wider mb-1">{t('clientView.nextStage')}</div>
          {nextStage ? (
            <div>
              <p className="text-sm font-bold text-[#374151]">{nextStage.title}</p>
              {nextStage.phase && <p className="text-[10px] text-[#9CA3AF] mt-0.5">{nextStage.phase}</p>}
            </div>
          ) : (
            <p className="text-sm text-[#9CA3AF]">—</p>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4 text-center">
          <div className="text-[10px] text-[#9CA3AF] font-semibold uppercase tracking-wider mb-1">{t('clientView.estCompletion')}</div>
          <p className="text-sm font-bold text-[#1A1A1A]">
            {estCompletion ? formatDateLabel(estCompletion) : 'TBC'}
          </p>
          {baselineEnd && (
            <p className="text-[10px] text-[#9CA3AF] mt-0.5">
              {t('clientView.original')}: {formatDateLabel(baselineEnd)}
            </p>
          )}
        </div>
      </div>

      {/* ── Schedule Update / Delay Explanation ── */}
      {delayExplanation && (
        <div className="bg-[#FEF0E7] rounded-2xl p-4 border border-[#FDBA74]">
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-error-warning-line text-[#E8621A]"></i>
            <span className="text-[#E8621A] text-sm font-bold">{t('clientView.scheduleUpdate')}</span>
          </div>
          <p className="text-[#374151] text-sm leading-relaxed">{delayExplanation}</p>
        </div>
      )}

      {/* ── Major Milestones ── */}
      {milestones.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <button
            onClick={() => setExpandedSection(expandedSection === 'milestones' ? null : 'milestones')}
            className="w-full flex items-center justify-between px-5 py-4 cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <i className="ri-flag-line text-[#E8621A]"></i>
              <span className="text-sm font-bold text-[#1A1A1A]">{t('clientView.keyScheduleItems')}</span>
            </div>
            <i className={`${expandedSection === 'milestones' ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-[#9CA3AF]`}></i>
          </button>
          {expandedSection === 'milestones' && (
            <div className="px-5 pb-4 space-y-3">
              {milestones.map((m, i) => {
                const isDone = m.is_complete;
                const isCurrent = currentStage?.id === m.id;
                return (
                  <div key={m.id} className="flex items-start gap-3">
                    <div className="flex flex-col items-center flex-shrink-0 w-6">
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center border-2 text-[10px] font-bold flex-shrink-0"
                        style={{
                          backgroundColor: isDone ? '#F0FDF4' : isCurrent ? '#FEF0E7' : '#F9FAFB',
                          borderColor: isDone ? '#BBF7D0' : isCurrent ? '#FDBA74' : '#E5E7EB',
                          color: isDone ? '#059669' : isCurrent ? '#E8621A' : '#9CA3AF',
                        }}
                      >
                        {isDone ? <i className="ri-check-line text-xs"></i> : i + 1}
                      </div>
                      {i < milestones.length - 1 && (
                        <div
                          className="w-0.5 flex-1 min-h-[12px]"
                          style={{ backgroundColor: isDone ? '#BBF7D0' : '#E5E7EB' }}
                        ></div>
                      )}
                    </div>
                    <div className={`flex-1 min-w-0 ${i < milestones.length - 1 ? 'pb-3' : 'pb-0'}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-sm font-semibold leading-tight ${isCurrent ? 'text-[#E8621A]' : isDone ? 'text-[#1A1A1A]' : 'text-[#9CA3AF]'}`}>
                          {m.title}
                        </span>
                        {m.phase && (
                          <span className="text-[10px] text-[#9CA3AF] whitespace-nowrap flex-shrink-0">{m.phase}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-[#9CA3AF]">
                        <span>{formatDateLabel(m.start_date)} — {formatDateLabel(m.end_date)}</span>
                        {isDone && m.actual_end_date && (
                          <span className="text-[#059669]">Done {formatDateLabel(m.actual_end_date)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Upcoming Work ── */}
      {upcomingItems.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <button
            onClick={() => setExpandedSection(expandedSection === 'upcoming' ? null : 'upcoming')}
            className="w-full flex items-center justify-between px-5 py-4 cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <i className="ri-time-line text-[#6B7280]"></i>
              <span className="text-sm font-bold text-[#1A1A1A]">{t('clientView.upcomingWork')}</span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F9FAFB] text-[#6B7280]">
                {upcomingItems.length}
              </span>
            </div>
            <i className={`${expandedSection === 'upcoming' ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-[#9CA3AF]`}></i>
          </button>
          {expandedSection === 'upcoming' && (
            <div className="px-5 pb-4 space-y-2">
              {upcomingItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setSelectedItem(item)}
                  className="w-full text-left flex items-center justify-between bg-[#F9FAFB] rounded-xl p-3 cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#1A1A1A] truncate">{item.title}</p>
                    {item.phase && <p className="text-[10px] text-[#9CA3AF]">{item.phase}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FEF0E7] text-[#E8621A] whitespace-nowrap">
                      {t('clientView.starts')} {formatDateLabel(item.start_date)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Recently Completed ── */}
      {recentlyCompleted.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <button
            onClick={() => setExpandedSection(expandedSection === 'completed' ? null : 'completed')}
            className="w-full flex items-center justify-between px-5 py-4 cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <i className="ri-check-double-line text-[#059669]"></i>
              <span className="text-sm font-bold text-[#1A1A1A]">{t('clientView.recentlyCompleted')}</span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F0FDF4] text-[#059669]">
                {recentlyCompleted.length}
              </span>
            </div>
            <i className={`${expandedSection === 'completed' ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-[#9CA3AF]`}></i>
          </button>
          {expandedSection === 'completed' && (
            <div className="px-5 pb-4 space-y-2">
              {recentlyCompleted.map((item) => (
                <div key={item.id} className="flex items-center justify-between bg-[#F0FDF4] rounded-xl p-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#1A1A1A] truncate">{item.title}</p>
                    {item.phase && <p className="text-[10px] text-[#9CA3AF]">{item.phase}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#BBF7D0] text-[#059669] whitespace-nowrap">
                      {t('clientView.done')}
                    </span>
                    {item.actual_end_date && (
                      <span className="text-[10px] text-[#9CA3AF] whitespace-nowrap">
                        {formatDateLabel(item.actual_end_date)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Schedule Updates ── */}
      <ClientScheduleUpdates projectId={items[0]?.project_id || ''} />

      {/* ── Item Detail Modal (read-only) ── */}
      {selectedItem && (
        <ClientItemDetailModal item={selectedItem} items={included} onClose={() => setSelectedItem(null)} />
      )}

      {/* ── Legend / Info ── */}
      <div className="bg-[#F9FAFB] rounded-2xl p-4">
        <p className="text-[10px] text-[#9CA3AF] font-semibold uppercase tracking-wider mb-2">{t('clientView.scheduleLegend')}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#6B7280]">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#059669]"></span>{t('clientView.legendCompleted')}</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#E8621A]"></span>{t('clientView.legendCurrent')}</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#D1D5DB]"></span>{t('clientView.legendUpcoming')}</span>
        </div>
        <p className="text-[10px] text-[#9CA3AF] mt-3 leading-relaxed">
          {t('clientView.scheduleNote')}
        </p>
      </div>
    </div>
  );
}

// ── Client Item Detail Modal (read-only, no technical terms) ──
interface ClientItemDetailModalProps {
  item: ScheduleItem;
  items: ScheduleItem[];
  onClose: () => void;
}

function ClientItemDetailModal({ item, items, onClose }: ClientItemDetailModalProps) {
  const { t } = useTranslation();
  const linked = getLinkedStageNames(item, [], items);

  return (
    <div className="fixed inset-0 z-[70] flex items-end lg:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white w-full lg:max-w-lg rounded-t-3xl lg:rounded-3xl overflow-hidden max-h-[85vh] flex flex-col">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
              style={{
                backgroundColor: item.is_complete ? '#F0FDF4' : '#F9FAFB',
                color: item.is_complete ? '#059669' : '#9CA3AF',
              }}
            >
              {item.is_complete ? t('clientView.completed') : t('clientView.upcoming')}
            </span>
            <h3 className="text-[#1A1A1A] font-bold text-sm truncate">{item.title}</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer flex-shrink-0 ml-2">
            <i className="ri-close-line text-[#374151]"></i>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 min-h-0">
          {item.phase && (
            <div className="flex items-center gap-2">
              <i className="ri-folder-line text-[#9CA3AF] text-sm"></i>
              <span className="text-[#6B7280] text-sm">{item.phase}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div className="bg-[#F9FAFB] rounded-xl p-3">
              <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-1">{t('clientView.expectedStart')}</p>
              <p className="text-[#374151] text-sm font-semibold">{formatDateFull(item.start_date)}</p>
            </div>
            <div className="bg-[#F9FAFB] rounded-xl p-3">
              <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-1">{t('clientView.expectedEnd')}</p>
              <p className="text-[#374151] text-sm font-semibold">{formatDateFull(item.end_date)}</p>
            </div>
            <div className="bg-[#F9FAFB] rounded-xl p-3">
              <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-1">{t('clientView.duration')}</p>
              <p className="text-[#374151] text-sm font-semibold">{item.duration_days} {t('clientView.workingDays')}</p>
            </div>
            <div className="bg-[#F9FAFB] rounded-xl p-3">
              <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-1">{t('clientView.progress')}</p>
              <p className="text-[#374151] text-sm font-semibold">{item.progress}%</p>
            </div>
          </div>

          {/* Progress bar */}
          <div>
            <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${item.progress}%`, backgroundColor: item.is_complete ? '#059669' : '#E8621A' }}
              />
            </div>
          </div>

          {/* Linked stages */}
          {(linked.previous || linked.next) && (
            <div className="bg-[#F9FAFB] rounded-xl p-3">
              <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-2">{t('clientView.linkedStages')}</p>
              <div className="space-y-1.5">
                {linked.previous && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-200 text-[#374151]">{t('clientView.previous')}</span>
                    <span className="text-[#374151]">{linked.previous}</span>
                  </div>
                )}
                {linked.next && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-200 text-[#374151]">{t('clientView.next')}</span>
                    <span className="text-[#374151]">{linked.next}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {item.delay_reason && (
            <div className="bg-[#FEF0E7] rounded-xl p-3 border border-[#FDE68A]">
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-information-line text-[#E8621A] text-sm"></i>
                <span className="text-[#E8621A] text-xs font-bold">{t('clientView.update')}</span>
              </div>
              <p className="text-[#374151] text-sm leading-relaxed">{item.delay_reason}</p>
            </div>
          )}

          {item.notes && (
            <div className="bg-[#F9FAFB] rounded-xl p-3">
              <p className="text-[#9CA3AF] text-[10px] font-semibold uppercase tracking-wider mb-1">{t('clientView.notes')}</p>
              <p className="text-[#6B7280] text-sm leading-relaxed">{item.notes}</p>
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full py-3 rounded-xl bg-[#F9FAFB] text-[#374151] text-sm font-bold cursor-pointer whitespace-nowrap"
          >
            {t('clientView.close')}
          </button>
        </div>
      </div>
    </div>
  );
}